/**
 * Returns a function, that, as long as it continues to be invoked, will not be triggered.
 * The function will be called after it stops being called for `wait` milliseconds.
 *
 * @param {Function} func the function to debounce
 * @param {number} wait the amount of time to wait in milliseconds
 * @returns the debounced function
 *
 * Usage:
 *
 * `debounce(this.myMethod(myParam1, myParam2), 250))`
 *
 * https://levelup.gitconnected.com/debounce-in-javascript-improve-your-applications-performance-5b01855e086
 * https://gist.github.com/treyhuffine/d628c0cd2e7d25f829159e08c29e92c0#file-debounce-description-js
 * https://davidwalsh.name/javascript-debounce-function
 */
export const debounce = (func, wait) => {
    let timeout;
    /*
      This is the function that is returned and will be executed many times
      We spread (...args) to capture any number of parameters we want to pass in
    */
    return (...args) => {
        /*
          The callback function to be executed after
          the debounce time has elapsed
        */
        const callFunc = () => {
            // null timeout to indicate the debounce ended
            timeout = null;
            // execute the callback
            func(...args);
        };
        /*
          This will reset the waiting every function execution.
          This is the step that prevents the function from
          being executed because it will never reach the
          inside of the previous `setTimeout`
        */
        clearTimeout(timeout);
        /*
          Restart the debounce waiting period.
          `setTimeout` returns a truthy value (it differs in web vs Node)
        */
        timeout = setTimeout(callFunc, wait);
    };
};
//# sourceMappingURL=debounce.js.map